import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import { PlansHierarchy } from '../plans/PlansHierarchy';
import { useAuth } from '../../../../hooks/useAuth';

export const WorkOrdersModal = ({ isOpen, onClose, maintenancePlan, onSave }) => {
  const auth = useAuth();
  const { isLoading, error } = auth;
  // Obtain user data from as many sourvces as possible (priority: apiUserData > serverAuth > user)
  const userData = auth?.apiUserData?.user || auth?.serverAuth?.user || auth?.user || {};
  const userName = userData.name || 'Usuario';
  const userEmail = userData.email || 'N/A';

  // Form state for work order
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    asset_id: '',
    maintenance_plan_id: '',
    priority: '',
    work_order_type: '',
    scheduled_date: '',
    estimated_hours: '',
  });

  // UI state
  const [isHierarchyOpen, setHierarchyOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [localModalError, setLocalModalError] = useState(null);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);
  const [selectedAssetDetails, setSelectedAssetDetails] = useState(null);

  const formRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalModalError(null);
      setFormErrors({});
      setFormData({
        title: '',
        description: '',
        asset_id: '',
        maintenance_plan_id: maintenancePlan?.id?.toString(),
        priority: '',
        work_order_type: '',
        scheduled_date: new Date().toISOString(),
        estimated_hours: '',
      });
      setSelectedAsset(null);
      setSelectedAssetDetails(null);
      setIsUpdating(true);
    }
  }, [isOpen]);

  // Update asset_id when asset is selected from hierarchy
  useEffect(() => {
    if (selectedAssetDetails?.ID) {
      setFormData(prev => ({
        ...prev,
        asset_id: selectedAssetDetails.ID.toString(),
      }));
      setIsUpdating(false);
    }
  }, [selectedAssetDetails]);

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length > 255) {
      errors.title = 'Title must be less than 255 characters';
    }

    if (!formData.asset_id.trim()) {
      errors.asset_id = 'Asset is required';
    }

    if (!formData.priority.trim()) {
      errors.priority = 'Priority is required';
    }

    if (!formData.work_order_type.trim()) {
      errors.work_order_type = 'Work order type is required';
    } else if (!['preventive', 'predictive'].includes(formData.work_order_type)) {
      errors.work_order_type = 'Type must be preventive or predictive';
    }

    if (!formData.estimated_hours.toString().trim()) {
      errors.estimated_hours = 'Estimated hours are required';
    } else if (isNaN(formData.estimated_hours) || parseFloat(formData.estimated_hours) <= 0) {
      errors.estimated_hours = 'Estimated hours must be a positive number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    let eventTime = formData.scheduled_date || ''
    if (!eventTime) {
      eventTime = new Date().toISOString()
    } else {
      // Validate if the date is valid
      try {
        const date = new Date(eventTime)
        if (isNaN(date.getTime())) {
          eventTime = new Date().toISOString()
        } else {
          eventTime = date.toISOString()
        }
      } catch {
        eventTime = new Date().toISOString()
      }
    }
    setLocalIsSubmitting(true);
    setLocalModalError(null);

    // To submit
    try {
      const workOrderData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        asset_id: parseInt(formData.asset_id.trim()),
        maintenance_plan_id: parseInt(formData.maintenance_plan_id?.trim()),
        priority: formData.priority.trim(),
        work_order_type: formData.work_order_type.trim(),
        scheduled_date: eventTime,
        estimated_hours: parseFloat(formData.estimated_hours),
      };

      console.log('Work order data to save:', workOrderData);
      await onSave(workOrderData);
      onClose();
    } catch (error) {
      setLocalModalError(error.message || 'Error creating work order');
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLocalIsSubmitting(false);
    }
  };

  // Update textfield per keypress
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      // Limit title to 255 characters
      [name]: name === 'title' ? value.slice(0, 255) : value,
    }));
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center gap-3 justify-center p-4 bg-black/60 backdrop-blur-sm">
      {/* Asset hierarchy modal (if open) */}
      <div className={isHierarchyOpen ? 'visible' : 'invisible'}>
        <PlansHierarchy
          assetId={selectedAsset}
          setAssetId={setSelectedAsset}
          setIsLoadingAsset={setIsLoadingAsset}
          setAssetDetails={setSelectedAssetDetails}
          selectMode="asset"
          rootAssetId={maintenancePlan?.asset_id}
        />
      </div>

      {/* Main modal */}
      <div
        className={`bg-gray-800 ${!isHierarchyOpen ? 'absolute left-1/2 -translate-x-1/2' : ''
          } rounded-2xl border border-gray-700 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-white">Create Work Order</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
            disabled={localIsSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Parent ID (only display, not editable) */}
        {formData.maintenance_plan_id && (
          <div className="bg-gray-700/50 rounded-xl px-4 py-2 mx-6 mt-6">
            <p className="text-sm text-gray-400 mb-1">Maintenance Plan</p>
            <p className="text-white font-medium">{maintenancePlan.name}</p>
            <p className="text-xs text-gray-500 mt-1">This work order will belong to the selected maintenance plan</p>
          </div>
        )}

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Global error message */}
            {localModalError && (
              <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                <p className="text-red-300 text-sm">{localModalError}</p>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                Work Order Information
              </h3>

              {/* Two‑column layout for basic fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    maxLength="255"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.title ? 'border-red-500' : 'border-gray-600'
                      }`}
                    placeholder="Work order title"
                    disabled={localIsSubmitting}
                  />
                  {formErrors.title && <p className="mt-1 text-sm text-red-400">{formErrors.title}</p>}
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Priority <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white ${formErrors.priority ? 'border-red-500' : 'border-gray-600'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="" disabled>Select priority</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                  {formErrors.priority && <p className="mt-1 text-sm text-red-400">{formErrors.priority}</p>}
                </div>

                {/* Asset (with hierarchy picker) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Asset <span className="text-red-400">*</span>
                  </label>
                  <div className="flex flex-row gap-2">
                    <p
                      className={`w-full px-4 py-2 rounded-lg border bg-gray-700 ${formErrors.asset_id ? 'border-red-500' : 'border-gray-600'
                        } ${formData.asset_id ? 'text-white' : 'text-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      {selectedAssetDetails
                        ? selectedAssetDetails.Location
                        : isLoadingAsset
                          ? <Loader2 className="w-4 h-4 animate-spin inline" />
                          : formData.asset_id
                            ? 'Asset selected'
                            : 'Not selected'}
                    </p>
                    <button
                      type="button"
                      onClick={() => setHierarchyOpen(!isHierarchyOpen)}
                      className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors"
                    >
                      {isHierarchyOpen ? 'Close' : 'Open'}
                    </button>
                  </div>
                  {formErrors.asset_id && <p className="mt-1 text-sm text-red-400">{formErrors.asset_id}</p>}
                </div>

                {/* Work Order Type (only preventive/predictive) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Work Order Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="work_order_type"
                    value={formData.work_order_type}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white ${formErrors.work_order_type ? 'border-red-500' : 'border-gray-600'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="" disabled>Select type</option>
                    <option value="preventive">Preventive</option>
                    <option value="predictive">Predictive</option>
                  </select>
                  {formErrors.work_order_type && <p className="mt-1 text-sm text-red-400">{formErrors.work_order_type}</p>}
                </div>

                {/* Scheduled Date (datetime‑local) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Scheduled Date <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="scheduled_date"
                    value={formData.scheduled_date
                      ? (() => {
                        try {
                          const date = new Date(formData.scheduled_date)
                          if (isNaN(date.getTime())) {
                            // If date invalid, use current date
                            return new Date().toISOString().slice(0, 16)
                          }
                          return date.toISOString().slice(0, 16)
                        } catch {
                          // If there is an error parsing, use the current date
                          return new Date().toISOString().slice(0, 16)
                        }
                      })()
                      : new Date().toISOString().slice(0, 16) // Default: current date and time
                    }
                    onChange={(e) => {
                      // Convert datetime-local value to ISO string
                      const isoValue = e.target.value ? new Date(e.target.value).toISOString() : new Date().toISOString()
                      setFormData({ ...formData, scheduled_date: isoValue })
                    }}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white ${formErrors.scheduled_date ? 'border-red-500' : 'border-gray-600'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    disabled={localIsSubmitting}
                  />
                  {formErrors.scheduled_date && <p className="mt-1 text-sm text-red-400">{formErrors.scheduled_date}</p>}
                </div>

                {/* Estimated Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Estimated Hours <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    name="estimated_hours"
                    value={formData.estimated_hours}
                    onChange={handleChange}
                    min="0.01"
                    step="0.01"
                    max="99.99"

                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.estimated_hours ? 'border-red-500' : 'border-gray-600'
                      }`}
                    placeholder="e.g., 2.5"
                    disabled={localIsSubmitting}
                  />
                  {formErrors.estimated_hours && <p className="mt-1 text-sm text-red-400">{formErrors.estimated_hours}</p>}
                </div>
              </div>

              {/* Description (full width) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Optional description"
                  disabled={localIsSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Footer buttons */}
          <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={localIsSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              disabled={localIsSubmitting || isUpdating}
            >
              {localIsSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};